import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import { contactInfo, formLocationOptions } from "@/mocks/ppcare";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db2fbngm252sfkho2330";

const contactRows = [
  { icon: "ri-phone-line", label: "Call Us", value: contactInfo.phone, href: `tel:${contactInfo.phone.replace(/[^0-9]/g, "")}` },
  { icon: "ri-mail-line", label: "Email Us", value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  { icon: "ri-time-line", label: "Office Hours", value: contactInfo.hours, href: "" },
];

type Status = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot: silently accept and pretend everything is fine.
    const honeypot = ((formData.get("company_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "Something went wrong. Please try again or give us a call."
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("Network error. Please check your connection and try again.");
    }
  };

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-24">
      <div className="container">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="flex flex-col justify-center">
              <p className="mb-3 font-label text-sm font-medium uppercase tracking-[0.2em] text-accent-600">
                Get In Touch
              </p>
              <h2 className="text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
                Request An <span className="text-accent-500">Appointment</span>
              </h2>
              <p className="mt-5 max-w-lg text-sm leading-relaxed text-foreground-600 md:text-base">
                Fill out the form and our team will reach out to confirm your visit — usually
                within one business day. Prefer to talk? Call or email us directly, or stop by any
                of our South Florida locations.
              </p>

              <div className="mt-8 flex flex-col gap-4">
                {contactRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-4"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className={`${row.icon} text-xl`} />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                        {row.label}
                      </p>
                      {row.href ? (
                        <a
                          href={row.href}
                          className="text-sm font-semibold text-foreground-900 transition-colors hover:text-primary-600 md:text-base"
                        >
                          {row.value}
                        </a>
                      ) : (
                        <p className="text-sm font-semibold text-foreground-900 md:text-base">
                          {row.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  Our Locations
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {contactInfo.cities.map((city) => (
                    <span
                      key={city}
                      className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-4 py-1.5 text-xs font-medium text-secondary-900"
                    >
                      <i className="ri-map-pin-line" />
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <form
                id="contact-appointment-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
                noValidate
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="first-name"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-600"
                    >
                      First Name
                    </label>
                    <input
                      id="first-name"
                      name="first_name"
                      type="text"
                      required
                      autoComplete="given-name"
                      placeholder="Jane"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="last-name"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-600"
                    >
                      Last Name
                    </label>
                    <input
                      id="last-name"
                      name="last_name"
                      type="text"
                      required
                      autoComplete="family-name"
                      placeholder="Doe"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-600"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="jane@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-600"
                    >
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      placeholder="(561) 000-0000"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="location"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-600"
                  >
                    Preferred Location
                  </label>
                  <select
                    id="location"
                    name="location"
                    defaultValue=""
                    className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="" disabled>
                      Select a location
                    </option>
                    {formLocationOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-600"
                  >
                    How Can We Help?
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us briefly about the reason for your visit..."
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <input
                  className="contact-assist"
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "success" && (
                  <p
                    role="status"
                    className="flex items-center gap-2 rounded-md border border-primary-300 bg-primary-100 px-4 py-3 text-sm font-medium text-primary-800"
                  >
                    <i className="ri-checkbox-circle-line text-lg" />
                    Thank you! Your request has been sent. We will contact you shortly.
                  </p>
                )}

                {status === "error" && (
                  <p
                    role="alert"
                    className="flex items-center gap-2 rounded-md border border-accent-300 bg-accent-100 px-4 py-3 text-sm font-medium text-accent-900"
                  >
                    <i className="ri-error-warning-line text-lg" />
                    {formError || "Something went wrong. Please try again."}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg" />
                      Send Request
                    </>
                  )}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}